// Le moteur d'une partie : ce que fait le téléphone quand le lecteur scanne un choix. Fonctions pures
// sur le livre compilé, sans dépendance : repris tel quel par l'appli compagnon.

import { normaliserCode } from './codes.js';
import { appliquer, suiteRetenue } from './conditions.js';
import type { ChoixCompile, Etat, Livre, SceneCompilee } from './types.js';

export type Refus =
  | 'code-inconnu'
  | 'pas-sur-cette-page'
  | 'partie-finie'
  | 'enigme-attendue'
  | 'pas-une-enigme'
  | 'aucune-suite'
  | 'etat-perime';

/** `page` vaut 0 quand l'état ne correspond plus à aucune scène du livre (état périmé). */
export type Resultat =
  | { ok: true; etat: Etat; page: number; scene: SceneCompilee }
  | { ok: false; refus: Refus; page: number };

const index = new WeakMap<Livre, Map<string, { scene: SceneCompilee; choix: ChoixCompile }>>();

function choixParCode(livre: Livre): Map<string, { scene: SceneCompilee; choix: ChoixCompile }> {
  let table = index.get(livre);
  if (!table) {
    table = new Map();
    for (const scene of livre.scenes) {
      for (const choix of scene.choix) table.set(choix.code, { scene, choix });
    }
    index.set(livre, table);
  }
  return table;
}

export function scene(livre: Livre, id: string): SceneCompilee {
  const trouvee = livre.scenes.find((candidate) => candidate.id === id);
  if (!trouvee) throw new Error(`scène inconnue : ${id}`);
  return trouvee;
}

/** L'état d'une partie qui commence : scène de départ, aucun indice, jauges à leur valeur de départ. */
export function demarrer(livre: Livre): Etat {
  const jauges: Record<string, number> = {};
  for (const jauge of livre.jauges) jauges[jauge.id] = jauge.depart;
  return { scene: livre.depart, indices: [], jauges };
}

/**
 * Trouve le choix scanné et vérifie qu'il est bien sur la page où en est le lecteur : un code lu sur
 * une autre page est refusé, c'est ce qui empêche de tricher en scannant la suite d'avance.
 */
function choixScanne(
  livre: Livre,
  etat: Etat,
  codeSaisi: string,
): { choix: ChoixCompile; courante: SceneCompilee } | { refus: Refus; page: number } {
  // Un état gardé par un navigateur peut viser une scène qu'une nouvelle version du livre n'a plus.
  const courante = livre.scenes.find((candidate) => candidate.id === etat.scene);
  if (!courante) return { refus: 'etat-perime', page: 0 };
  if (courante.fin) return { refus: 'partie-finie', page: courante.page };
  const trouve = choixParCode(livre).get(normaliserCode(codeSaisi));
  if (!trouve) return { refus: 'code-inconnu', page: courante.page };
  if (trouve.scene.id !== courante.id) return { refus: 'pas-sur-cette-page', page: courante.page };
  return { choix: trouve.choix, courante };
}

/** Le lecteur a scanné un choix ordinaire : la première suite dont la condition est vraie décide. */
export function choisir(livre: Livre, etat: Etat, codeSaisi: string): Resultat {
  const lu = choixScanne(livre, etat, codeSaisi);
  if ('refus' in lu) return { ok: false, refus: lu.refus, page: lu.page };
  if (lu.choix.enigme) return { ok: false, refus: 'enigme-attendue', page: lu.courante.page };
  const suite = suiteRetenue(lu.choix.suites ?? [], etat);
  if (!suite) return { ok: false, refus: 'aucune-suite', page: lu.courante.page };
  const suivant = appliquer(suite, etat, livre);
  const arrivee = scene(livre, suivant.scene);
  return { ok: true, etat: suivant, page: arrivee.page, scene: arrivee };
}

/**
 * Réponse comparée sans accents, sans espaces ni ponctuation, sans casse : « 3 h 12 » vaut « 3H12 ».
 * Les ligatures, que la décomposition Unicode ne sépare pas, sont écrites en deux lettres : « cœur »
 * vaut « coeur ».
 */
export function normaliserReponse(reponse: string): string {
  return reponse
    .toLowerCase()
    .replace(/œ/g, 'oe')
    .replace(/æ/g, 'ae')
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/[^a-z0-9]/g, '');
}

/** Le lecteur a scanné une énigme et tapé sa réponse. */
export function repondre(livre: Livre, etat: Etat, codeSaisi: string, reponse: string): Resultat {
  const lu = choixScanne(livre, etat, codeSaisi);
  if ('refus' in lu) return { ok: false, refus: lu.refus, page: lu.page };
  const enigme = lu.choix.enigme;
  if (!enigme) return { ok: false, refus: 'pas-une-enigme', page: lu.courante.page };
  // Une réponse vide après normalisation (« ... ») n'est jamais juste, même face à une solution mal écrite.
  const lue = normaliserReponse(reponse);
  const juste = lue !== '' && enigme.solutions.some((solution) => normaliserReponse(solution) === lue);
  const suivant = appliquer(juste ? enigme.juste : enigme.faux, etat, livre);
  const arrivee = scene(livre, suivant.scene);
  return { ok: true, etat: suivant, page: arrivee.page, scene: arrivee };
}
